// src/pages/documents/InvoiceScan.jsx
// 📷 Scan a supplier invoice (photo/PDF) → n8n + OpenAI vision extract →
// review/fix lines → import into the open Purchase In draft.
// Unknown products are created on the fly (cost set, sale price left for later).
import React, { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';

const ACCENT = '#2e9f86';
// read by the MMM AI service (needs the ai_scan add-on); a local install forwards it to the cloud
const SCAN_PATH = '/api/analysis/scan';

// downscale photos so upload + vision stay fast/cheap (PDFs pass through)
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  if (file.type === 'application/pdf') {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = reject;
    fr.readAsDataURL(file);
    return;
  }
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const MAX = 1600;
    const scale = Math.min(1, MAX / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.82));
  };
  img.onerror = reject;
  img.src = url;
});

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

export default function InvoiceScan({ docId, products, accounts, accountId, rate, docCurrency = 'USD', categories = [], priceLevels = [], plEnabled = false, onAccountMatched, onImported, onClose }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState('pick'); // pick | scanning | review | importing | done
  const [invoice, setInvoice] = useState(null);
  const [invCur, setInvCur] = useState('USD'); // currency detected ON the paper invoice
  const [lines, setLines] = useState([]);
  const [vendorAcc, setVendorAcc] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  // convert a value from the invoice's currency into the DOCUMENT's currency
  const convertVal = (v, from) => {
    if (v == null || v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    if (from === docCurrency) return n;
    return from === 'LBP'
      ? Math.round((n / (rate || 89500)) * 100) / 100   // LBP → USD (2 decimals)
      : Math.round(n * (rate || 89500));                 // USD → LBP (whole)
  };

  // user corrects the detected invoice currency → re-convert all prices from the raw values
  const changeInvCur = (c) => {
    setInvCur(c);
    setLines(ls => ls.map(l => ({ ...l, price: l.rawPrice == null || l.rawPrice === '' ? l.price : convertVal(l.rawPrice, c) })));
  };

  const matchProduct = (name) => {
    const n = norm(name);
    if (!n) return null;
    return products.find(p => norm(p.name) === n)
      || (n.length > 3 ? products.find(p => norm(p.name).includes(n) || n.includes(norm(p.name))) : null)
      || null;
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setStep('scanning');
    try {
      const dataUrl = await fileToDataUrl(file);
      let data;
      try {
        data = (await api.post(SCAN_PATH, { image_url: dataUrl, file_name: file.name }, { timeout: 180000 })).data;
      } catch (e) { throw new Error(e.response?.data?.error || e.message); }
      if (data?.error) throw new Error(data.error);
      if (!Array.isArray(data.items) || data.items.length === 0) throw new Error('No items found on this invoice — try a clearer photo.');

      setInvoice(data);
      const detected = data.currency === 'LBP' ? 'LBP' : 'USD';
      setInvCur(detected);
      setLines(data.items.map(it => {
        // barcode match beats name match — it's exact
        const byBarcode = it.barcode ? products.find(p => String(p.barcode || '').trim() === String(it.barcode).trim()) : null;
        const m = byBarcode || matchProduct(it.name);
        return {
          include: true,
          name: it.name || '',
          qty: it.qty || 1,
          rawPrice: it.price ?? '',                    // as written on the paper
          price: convertVal(it.price ?? '', detected), // in the DOCUMENT's currency
          salePrice: '',                 // sale price (USD) — required for NEW products
          barcode: it.barcode || '',     // from the bill when visible — optional
          catId: '',                     // category for NEW products — optional
          levels: {},                    // price-level prices (USD) — when feature is on
          matched: m ? { id: m._id, name: m.name } : null,
        };
      }));
      // vendor → supplier account suggestion
      const v = norm(data.vendor);
      const acc = v ? accounts.find(a => norm(a.name) === v) || (v.length > 3 ? accounts.find(a => norm(a.name).includes(v) || v.includes(norm(a.name))) : null) : null;
      setVendorAcc(acc || null);
      setStep('review');
    } catch (e) {
      setError(e.message || 'Could not read the invoice.');
      setStep('pick');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const setLine = (i, k, v) => setLines(ls => ls.map((l, idx) => {
    if (idx !== i) return l;
    const next = { ...l, [k]: v };
    // editing the name re-checks the match live (so fixing a typo finds the existing product)
    if (k === 'name') {
      const m = matchProduct(v);
      next.matched = m ? { id: m._id, name: m.name } : null;
    }
    return next;
  }));
  const setLevel = (i, levelId, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, levels: { ...l.levels, [levelId]: v } } : l));

  // barcode must not clash with an existing product or another line in this scan
  const barcodeTaken = (i) => {
    const b = String(lines[i]?.barcode || '').trim();
    if (!b) return false;
    return products.some(p => String(p.barcode || '').trim() === b)
      || lines.some((o, j) => j !== i && o.include && String(o.barcode || '').trim() === b);
  };

  const lineReady = (l, i) => l.include && norm(l.name) && Number(l.qty) > 0 && l.price !== '' && Number(l.price) >= 0
    && (l.matched || (l.salePrice !== '' && Number(l.salePrice) >= 0 && !barcodeTaken(i))); // new products need a sale price + free barcode
  const included = lines.filter((l, i) => lineReady(l, i));
  const missingSalePrice = lines.filter(l => l.include && norm(l.name) && !l.matched && (l.salePrice === '' || Number(l.salePrice) < 0)).length;
  // items are ALWAYS added in the document's currency — never mixed
  const currency = docCurrency;

  const runImport = async () => {
    setStep('importing');
    setProgress({ done: 0, total: included.length });
    let added = 0, createdProducts = 0; const failed = [];

    // link supplier account if matched and the doc has none
    if (vendorAcc && !accountId) {
      try {
        await api.put(`/api/documents/${docId}`, { account_id: vendorAcc._id });
        onAccountMatched?.(vendorAcc._id);
      } catch { /* non-fatal */ }
    }

    const createdByName = {}; // names created during THIS import — never create twice

    for (let i = 0; i < included.length; i++) {
      const l = included[i];
      try {
        let productId = l.matched?.id;
        let productName = l.matched?.name || l.name.trim();
        if (!productId) {
          // duplicate protection: exact-name check against existing products + this batch
          const key = norm(l.name);
          const existing = products.find(p => norm(p.name) === key);
          if (existing) {
            productId = existing._id;
            productName = existing.name;
          } else if (createdByName[key]) {
            productId = createdByName[key].id;
            productName = createdByName[key].name;
          } else {
            const salePrice = Number(l.salePrice) || 0;
            // price levels: only filled values, stored as {usd, lbp} like the product form
            const price_levels = {};
            if (plEnabled) {
              for (const [lvlId, v] of Object.entries(l.levels || {})) {
                const usd = Number(v);
                if (usd > 0) price_levels[lvlId] = { usd, lbp: Math.round(usd * (rate || 89500)) };
              }
            }
            const res = await api.post('/api/products', {
              name: l.name.trim(), type: 'simple',
              barcode: String(l.barcode || '').trim(),
              category_id: l.catId || null,
              cost: Number(l.price), cost_currency: currency,
              price_usd: salePrice, price_lbp: Math.round(salePrice * (rate || 89500)),
              ...(plEnabled && Object.keys(price_levels).length ? { price_levels } : {}),
              stock: 0, manage_stock: true, unit: 'piece',
              dedupe_by_name: true, // server returns existing product if the name already exists
            });
            const p = res.data?.data ?? res.data;
            productId = p._id;
            productName = p.name;
            createdByName[key] = { id: productId, name: productName };
            createdProducts++;
          }
        }
        await api.post(`/api/documents/${docId}/items`, {
          product_id: productId,
          product_name: productName,
          quantity: Number(l.qty),
          price: Number(l.price),
          price_currency: currency,
          cost: Number(l.price),
          cost_currency: currency,
          tax: 0, discount: 0,
        });
        added++;
      } catch (err) {
        failed.push({ name: l.name, error: err.response?.data?.error || 'error' });
      }
      setProgress({ done: i + 1, total: included.length });
    }

    queryClient.invalidateQueries(['products']);
    setResult({ added, createdProducts, failed });
    setStep('done');
    onImported?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[720px] max-w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-lg text-gray-800">📷 Scan Supplier Invoice</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 'pick' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">🧾</div>
              <p className="font-semibold text-gray-800 mb-1">Photograph the supplier's invoice</p>
              <p className="text-sm text-gray-500 mb-5">Arabic or English, photo or PDF. You'll review every line before anything is added.</p>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
                onChange={e => handleFile(e.target.files?.[0])} className="hidden" id="inv-scan-file" />
              <label htmlFor="inv-scan-file"
                className="inline-block px-6 py-3 rounded-lg text-white font-bold cursor-pointer" style={{ background: ACCENT }}>
                📷 Take / choose photo
              </label>
              {error && <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 inline-block">{error}</div>}
            </div>
          )}

          {step === 'scanning' && (
            <div className="text-center py-16">
              <div className="w-10 h-10 mx-auto border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="font-semibold text-gray-700">Reading the invoice…</p>
              <p className="text-xs text-gray-400 mt-1">This takes ~10–20 seconds</p>
            </div>
          )}

          {step === 'review' && invoice && (
            <div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 mb-3">
                {invoice.vendor && <span>🏢 <b>{invoice.vendor}</b>{vendorAcc ? <span className="text-emerald-600"> → account "{vendorAcc.name}" {accountId ? '' : 'will be set'}</span> : <span className="text-gray-400"> (no matching supplier account)</span>}</span>}
                {invoice.invoice_number && <span># {invoice.invoice_number}</span>}
                {invoice.date && <span>📅 {invoice.date}</span>}
                {invoice.total != null && <span>Total on paper: <b>{invoice.total} {invCur}</b></span>}
                <span className="flex items-center gap-1.5">
                  💱 Invoice currency:
                  <select value={invCur} onChange={e => changeInvCur(e.target.value)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs font-bold bg-white">
                    <option value="USD">USD</option>
                    <option value="LBP">LBP</option>
                  </select>
                  {invCur !== docCurrency && (
                    <em className="text-emerald-700 not-italic text-xs font-semibold">
                      → converted to {docCurrency} @ {Number(rate || 89500).toLocaleString('en-US')}
                    </em>
                  )}
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: 340 }}>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-2"></th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-500">Item</th>
                      <th className="text-right px-2 py-2 font-semibold text-gray-500">Qty</th>
                      <th className="text-right px-2 py-2 font-semibold text-gray-500">Unit cost ({currency})</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-500">Match</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.map((l, i) => (
                      <React.Fragment key={i}>
                        <tr className={!l.include ? 'opacity-40' : ''}>
                          <td className="px-2 py-1.5"><input type="checkbox" checked={l.include} onChange={e => setLine(i, 'include', e.target.checked)} /></td>
                          <td className="px-2 py-1.5"><input value={l.name} onChange={e => setLine(i, 'name', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1" /></td>
                          <td className="px-2 py-1.5"><input type="number" min="0.001" step="any" value={l.qty} onChange={e => setLine(i, 'qty', e.target.value)} className="w-16 border border-gray-200 rounded px-2 py-1 text-right" /></td>
                          <td className="px-2 py-1.5"><input type="number" min="0" step="any" value={l.price} onChange={e => setLine(i, 'price', e.target.value)} className="w-24 border border-gray-200 rounded px-2 py-1 text-right" /></td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {l.matched
                              ? <span className="text-emerald-600 font-semibold">✓ {l.matched.name}</span>
                              : <span className="text-blue-600 font-semibold">🆕 new product</span>}
                          </td>
                        </tr>
                        {/* new product → extra fields row */}
                        {l.include && !l.matched && (
                          <tr className="bg-blue-50/50">
                            <td></td>
                            <td colSpan={4} className="px-2 pt-1 pb-2">
                              <div className="flex flex-wrap items-end gap-x-3 gap-y-1.5">
                                <label className="flex flex-col gap-0.5 text-[10px] font-semibold text-gray-500">
                                  Sale price ($) *
                                  <input type="number" min="0" step="0.01" value={l.salePrice} placeholder="required"
                                    onChange={e => setLine(i, 'salePrice', e.target.value)}
                                    className={`w-20 rounded px-2 py-1 text-right text-xs font-normal border ${l.salePrice === '' ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                                </label>
                                <label className="flex flex-col gap-0.5 text-[10px] font-semibold text-gray-500">
                                  Barcode (optional)
                                  <input value={l.barcode} placeholder="from bill / scanner"
                                    onChange={e => setLine(i, 'barcode', e.target.value.trim())}
                                    className={`w-32 rounded px-2 py-1 text-xs font-mono font-normal border ${barcodeTaken(i) ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                                </label>
                                <label className="flex flex-col gap-0.5 text-[10px] font-semibold text-gray-500">
                                  Category
                                  <select value={l.catId} onChange={e => setLine(i, 'catId', e.target.value)}
                                    className="w-28 rounded px-1.5 py-1 text-xs font-normal border border-gray-300 bg-white">
                                    <option value="">— none —</option>
                                    {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                  </select>
                                </label>
                                {plEnabled && priceLevels.map(lvl => (
                                  <label key={lvl._id} className="flex flex-col gap-0.5 text-[10px] font-semibold text-gray-500">
                                    {(lvl.name_en || lvl.name || 'Level')} ($)
                                    <input type="number" min="0" step="0.01" value={l.levels[lvl._id] ?? ''}
                                      onChange={e => setLevel(i, lvl._id, e.target.value)}
                                      className="w-20 rounded px-2 py-1 text-right text-xs font-normal border border-gray-300" />
                                  </label>
                                ))}
                                {barcodeTaken(i) && <span className="text-red-600 text-[10px] font-bold self-center">⚠ barcode already used</span>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              {missingSalePrice > 0
                ? <p className="text-xs text-red-600 font-semibold mt-2">⚠️ {missingSalePrice} new item(s) need a sale price before importing.</p>
                : <p className="text-xs text-gray-400 mt-2">✓ Existing products are matched by name and never duplicated — only their purchase line is added.</p>}
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-12">
              <p className="font-semibold text-gray-800 mb-3">Adding {progress.done} / {progress.total}…</p>
              <div className="w-72 mx-auto bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="h-3 rounded-full transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, background: ACCENT }} />
              </div>
            </div>
          )}

          {step === 'done' && result && (
            <div className="text-center py-10">
              <div className="text-4xl mb-3">{result.failed.length === 0 ? '🎉' : '⚠️'}</div>
              <p className="font-bold text-lg text-gray-800">{result.added} items added to the document</p>
              <p className="text-sm text-gray-500 mt-1">
                {result.createdProducts > 0 && <>{result.createdProducts} new products created · </>}
                {result.failed.length > 0 && <span className="text-red-600">{result.failed.length} failed</span>}
              </p>
              {result.failed.length > 0 && (
                <div className="mt-3 text-left max-w-sm mx-auto text-xs text-red-600 bg-red-50 rounded-lg p-3 max-h-28 overflow-y-auto">
                  {result.failed.map((f, i) => <div key={i}>• {f.name}: {f.error}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t">
          {step === 'review' && (
            <>
              <button onClick={() => { setStep('pick'); setInvoice(null); setLines([]); }} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg">← Rescan</button>
              <button onClick={runImport} disabled={included.length === 0}
                className="flex-1 px-4 py-2 text-white text-sm font-bold rounded-lg disabled:opacity-40" style={{ background: ACCENT }}>
                Add {included.length} items to document
              </button>
            </>
          )}
          {(step === 'pick' || step === 'done') && (
            <button onClick={onClose} className="ml-auto px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}
